import { ACTIONS } from "app/store/actions"
import { deepCopy } from "app/tools/common"
import { Action, Reducer } from "redux"
import { IUiState, MAX_TABLET_WIDTH_IN_PX } from "../model"
import {
  IActionOpenColorPickerPopup,
  IActionSetContainerSize,
  IActionSetIsDarkPreview,
  IActionSetIsSavingConfig,
  IActionSetMenuSize,
  IActionSetMenus,
  IActionSetTempUiConfig,
  IActionSetTheme,
  IActionSetUiConfigTranslations,
  IActionSetUiConfigUndoRedoState
} from "./actions"
import { getContentWidth, getDefaultUiState, getScreenType } from "./state"

export const uiReducer: Reducer<IUiState, Action> = (state: IUiState | undefined, action: Action): IUiState => {
  if (!state) {
    state = getDefaultUiState()
  }
  switch (action.type) {
    case ACTIONS.APP_UI_RESET_STATE: {
      return getDefaultUiState()
    }

    case ACTIONS.APP_UI_SET_THEME: {
      const { theme, config } = action as IActionSetTheme
      return {
        ...state,
        theme,
        config,
        tempConfig: deepCopy(config),
        uiConfigUndoRedoHistory: [deepCopy(config)],
        uiConfigUndoRedoIndex: 0,
        isSavingConfig: false
      }
    }

    case ACTIONS.APP_UI_SET_UI_CONFIG_TRANSLATIONS: {
      const { translations } = action as IActionSetUiConfigTranslations
      return {
        ...state,
        config: { ...state.config, translations },
        tempConfig: { ...state.tempConfig, translations: deepCopy(translations) }
      }
    }

    case ACTIONS.APP_UI_SET_IS_DARK_PREVIEW: {
      const { isDarkPreview } = action as IActionSetIsDarkPreview
      return { ...state, isDarkPreview }
    }

    case ACTIONS.APP_UI_SET_TEMP_UI_CONFIG: {
      const { uiConfig } = action as IActionSetTempUiConfig
      return { ...state, tempConfig: uiConfig }
    }

    case ACTIONS.APP_UI_SET_UNDO_REDO_UI_CONFIG_STATE: {
      const { newUiConfig, history, index } = action as IActionSetUiConfigUndoRedoState
      return { ...state, tempConfig: newUiConfig, uiConfigUndoRedoHistory: history, uiConfigUndoRedoIndex: index }
    }

    case ACTIONS.APP_UI_SET_CONTAINER_SIZE: {
      let { width, height } = action as IActionSetContainerSize
      width = typeof width === "number" ? width : state.container.width
      height = typeof height === "number" ? height : state.container.height
      const wasDesktopSize: boolean = state.container.width > MAX_TABLET_WIDTH_IN_PX
      const isNowSmallerThanDesktop: boolean = width <= MAX_TABLET_WIDTH_IN_PX
      const isMenuOpened: boolean = wasDesktopSize && isNowSmallerThanDesktop ? false : state.isMenuOpened
      const screenType = getScreenType()
      return {
        ...state,
        isMenuOpened,
        screenType,
        contentWidth: getContentWidth(screenType, isMenuOpened),
        container: {
          width,
          height
        }
      }
    }

    case ACTIONS.APP_UI_SET_MENU_SIZE: {
      const { isOpened } = action as IActionSetMenuSize
      return {
        ...state,
        contentWidth: getContentWidth(state.screenType, isOpened),
        isMenuOpened: isOpened
      }
    }

    case ACTIONS.APP_UI_SET_MENUS: {
      const { menus } = action as IActionSetMenus
      return {
        ...state,
        menus
      }
    }

    case ACTIONS.APP_UI_SET_IS_SAVING_CONFIG: {
      const { isSaving } = action as IActionSetIsSavingConfig
      return {
        ...state,
        isSavingConfig: isSaving
      }
    }

    case ACTIONS.APP_UI_OPEN_COLOR_PICKER_POPUP: {
      const { hexColor, setHexColor, title } = action as IActionOpenColorPickerPopup
      return {
        ...state,
        colorPickerPopup: {
          isOpened: true,
          hexColor,
          setHexColor,
          title
        }
      }
    }

    case ACTIONS.APP_UI_CLOSE_COLOR_PICKER_POPUP: {
      return {
        ...state,
        colorPickerPopup: {
          ...state.colorPickerPopup,
          isOpened: false,
          hexColor: "#ffffff",
          title: "",
          setHexColor: () => undefined
        }
      }
    }

    default:
      return state
  }
}
